import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import tributarioService from '../services/tributarioService';
import Loading from '../components/Loading';
import './RetencionesPage.css';

const ensureArray = (v) => Array.isArray(v) ? v : [];

const RetencionesPage = () => {
  const [retenciones, setRetenciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroPeriodo, setFiltroPeriodo] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');

  const [form, setForm] = useState({
    documento_legal: '', tipo_retencion: 'honorarios', porcentaje: 13, monto_base: '', monto_retenido: '', estado_pago: 'pendiente',
  });

  useEffect(() => { cargarRetenciones(); }, []);

  const cargarRetenciones = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filtroTipo) filters.tipo_retencion = filtroTipo;
      if (filtroPeriodo) filters.periodo = filtroPeriodo;
      if (filtroEstado) filters.estado_pago = filtroEstado;
      const res = await tributarioService.retenciones.getAll(filters);
      setRetenciones(ensureArray(res.data.results || res.data));
    } catch { toast.error('Error al cargar retenciones'); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargarRetenciones(); }, [filtroTipo, filtroPeriodo, filtroEstado]);

  const handleSave = async () => {
    try {
      if (editing) {
        await tributarioService.retenciones.update(editing.id, form);
        toast.success('Retencion actualizada');
      } else {
        await tributarioService.retenciones.create(form);
        toast.success('Retencion creada');
      }
      setModalOpen(false);
      setEditing(null);
      cargarRetenciones();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta retencion?')) return;
    try { await tributarioService.retenciones.delete(id); toast.success('Eliminada'); cargarRetenciones(); }
    catch { toast.error('Error al eliminar'); }
  };

  const handleEdit = (r) => {
    setEditing(r);
    setForm({ documento_legal: r.documento_legal?.id || r.documento_legal || '', tipo_retencion: r.tipo_retencion, porcentaje: r.porcentaje, monto_base: r.monto_base, monto_retenido: r.monto_retenido, estado_pago: r.estado_pago });
    setModalOpen(true);
  };

  const resetForm = () => setForm({ documento_legal: '', tipo_retencion: 'honorarios', porcentaje: 13, monto_base: '', monto_retenido: '', estado_pago: 'pendiente' });

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);

  if (loading) return <Loading />;

  return (
    <div className="retenciones-page">
      <div className="page-header">
        <h1>Retenciones</h1>
        <button className="btn-primary" onClick={() => { resetForm(); setEditing(null); setModalOpen(true); }}><FiPlus /> Nueva Retencion</button>
      </div>

      <div className="filtros-container">
        <select className="select-filtro" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          <option value="">Todos los tipos</option>
          <option value="honorarios">Honorarios</option>
          <option value="arriendo">Arriendo</option>
          <option value="servicios">Servicios</option>
        </select>
        <select className="select-filtro" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="pagada">Pagada</option>
        </select>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr><th>Documento</th><th>Tipo</th><th>%</th><th style={{ textAlign: 'right' }}>Base</th><th style={{ textAlign: 'right' }}>Retenido</th><th>Estado</th><th style={{ textAlign: 'right' }}>Acciones</th></tr>
          </thead>
          <tbody>
            {retenciones.length === 0 ? (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Sin retenciones</td></tr>
            ) : retenciones.map(r => (
              <tr key={r.id}>
                <td>{r.documento_legal_numero || r.documento_legal || '-'}</td>
                <td><span className="badge">{r.tipo_retencion_display || r.tipo_retencion}</span></td>
                <td>{r.porcentaje}%</td>
                <td className="mono right">{formatCurrency(r.monto_base)}</td>
                <td className="mono right bold">{formatCurrency(r.monto_retenido)}</td>
                <td><span className={`badge ${r.estado_pago === 'pagada' ? 'badge-green' : 'badge-yellow'}`}>{r.estado_pago}</span></td>
                <td>
                  <div className="acciones-cell">
                    <button className="btn-icon" onClick={() => handleEdit(r)}><FiEdit2 /></button>
                    <button className="btn-icon btn-danger" onClick={() => handleDelete(r.id)}><FiTrash2 /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{editing ? 'Editar Retencion' : 'Nueva Retencion'}</h2>
            <div className="form-grid">
              <div className="form-group"><label>Tipo Retencion</label>
                <select value={form.tipo_retencion} onChange={e => setForm({ ...form, tipo_retencion: e.target.value })}>
                  <option value="honorarios">Honorarios</option><option value="arriendo">Arriendo</option><option value="servicios">Servicios</option>
                </select>
              </div>
              <div className="form-group"><label>Porcentaje</label>
                <input type="number" value={form.porcentaje} onChange={e => setForm({ ...form, porcentaje: e.target.value })} />
              </div>
              <div className="form-group"><label>Monto Base</label>
                <input type="number" value={form.monto_base} onChange={e => setForm({ ...form, monto_base: e.target.value, monto_retenido: (e.target.value * form.porcentaje / 100).toFixed(0) })} />
              </div>
              <div className="form-group"><label>Monto Retenido</label>
                <input type="number" value={form.monto_retenido} onChange={e => setForm({ ...form, monto_retenido: e.target.value })} />
              </div>
              <div className="form-group"><label>Estado</label>
                <select value={form.estado_pago} onChange={e => setForm({ ...form, estado_pago: e.target.value })}>
                  <option value="pendiente">Pendiente</option><option value="pagada">Pagada</option>
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleSave}>{editing ? 'Actualizar' : 'Crear'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetencionesPage;
